/**
 * Chapter CRUD — spec §3.3 projects/{projectId}/chapters/{chapterId}
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { Chapter, ChapterStatus } from "@/types";

function chaptersRef(projectId: string) {
  return collection(db, "projects", projectId, "chapters");
}

export async function createChapter(
  projectId: string,
  data: Omit<Chapter, "id" | "project_id" | "created_at" | "updated_at">,
): Promise<string> {
  const docRef = await addDoc(chaptersRef(projectId), {
    ...data,
    project_id: projectId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return docRef.id;
}

export async function getChapter(projectId: string, chapterId: string): Promise<Chapter | null> {
  const snap = await getDoc(doc(db, "projects", projectId, "chapters", chapterId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Chapter;
}

export async function getChapters(projectId: string): Promise<Chapter[]> {
  const q = query(chaptersRef(projectId), orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Chapter);
}

export function subscribeChapters(
  projectId: string,
  onData: (chapters: Chapter[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const q = query(chaptersRef(projectId), orderBy("order", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Chapter));
    },
    onError,
  );
}

export async function updateChapter(
  projectId: string,
  chapterId: string,
  updates: Partial<Pick<Chapter, "title" | "summary" | "word_count" | "status" | "emotional_beat">>,
): Promise<void> {
  await updateDoc(doc(db, "projects", projectId, "chapters", chapterId), {
    ...updates,
    updated_at: serverTimestamp(),
  });
}

export async function updateChapterStatus(
  projectId: string,
  chapterId: string,
  status: ChapterStatus,
): Promise<void> {
  await updateChapter(projectId, chapterId, { status });
}

export async function reorderChapters(
  projectId: string,
  chapterOrder: { id: string; order: number }[],
): Promise<void> {
  const batch = writeBatch(db);
  for (const { id, order } of chapterOrder) {
    batch.update(doc(db, "projects", projectId, "chapters", id), {
      order,
      updated_at: serverTimestamp(),
    });
  }
  await batch.commit();
}
